"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ChartComponent = void 0;
const rc_js_util_1 = require("rc-js-util");
const frame_provider_1 = require("../update/frame-provider");
const on_plot_attach_changed_1 = require("../plot/events/on-plot-attach-changed");
const on_canvas_resized_1 = require("../templating/events/on-canvas-resized");
const on_plot_requires_update_1 = require("../plot/events/on-plot-requires-update");
const on_dpr_changed_1 = require("../templating/events/on-dpr-changed");
const e_entity_update_flag_1 = require("../update/e-entity-update-flag");
const chart_transform_factory_1 = require("./chart-transform-factory");
const on_graphics_component_added_1 = require("../rendering/events/on-graphics-component-added");
const on_renderer_context_restored_1 = require("../rendering/events/on-renderer-context-restored");
const on_renderer_context_lost_1 = require("../rendering/events/on-renderer-context-lost");
const emit_to_plots_1 = require("../eventing/emit-to-plots");
const on_entity_removed_1 = require("../plot/events/on-entity-removed");
const on_entity_added_1 = require("../plot/events/on-entity-added");
/**
 * @public
 * {@inheritDoc IChartComponent}
 */
class ChartComponent extends rc_js_util_1.AOnDestroy {
    constructor(attachPoint, renderer, eventService, config, contextAdapter, frameProvider) {
        super();
        this.plotCollection = new rc_js_util_1.DirtyCheckedUniqueCollection();
        this.contextLost = false;
        this.transformProvider = null;
        this.attachedEntities = new rc_js_util_1.ReferenceCounter((entity) => {
            const layouts = this.renderer.sharedState.entityBuffers.getLayouts(entity);
            if (layouts == null) {
                return;
            }
            layouts.forEach((layout) => {
                this.renderer.destroyBuffers(layout.getBuffers());
            });
            this.renderer.sharedState.entityBuffers.clearLayouts(entity);
        });
        this.contextAdapter = contextAdapter;
        this.renderer = renderer;
        this.eventService = eventService;
        this.updateOptions = config.updateOptions;
        this.attachPoint = attachPoint;
        this.changeIdFactory = config.changeIdFactory;
        this.frameProvider = frameProvider !== null && frameProvider !== void 0 ? frameProvider : this.createDefaultFrameProvider(config);
        this.registerEventHandlers();
    }
    cancelNextFrame() {
        this.frameProvider.cancelNextFrame();
    }
    updateOnNextFrame(plot, updateInteractionHandler = true) {
        updateInteractionHandler = Boolean(updateInteractionHandler);
        if (plot != null && !this.updateOptions.updateAllPlotsOnDraw) {
            this.frameProvider.updateOnNextFrame(plot, updateInteractionHandler);
        }
        else {
            const plots = this.plotCollection.getArray();
            for (let i = 0, iEnd = plots.length; i < iEnd; ++i) {
                this.frameProvider.updateOnNextFrame(plots[i], updateInteractionHandler);
            }
        }
    }
    updateImmediate(plot) {
        if (this.contextLost) {
            return;
        }
        if (this.updateOptions.updateDimsOnDraw) {
            this.resize();
        }
        const canvasDims = this.attachPoint.canvasDims;
        if (plot != null) {
            if (this.plotCollection.has(plot)) {
                this.drawPlot(plot, canvasDims);
            }
        }
        else {
            const plots = this.plotCollection.getArray();
            for (let i = 0, iEnd = plots.length; i < iEnd; ++i) {
                const plot = plots[i];
                this.drawPlot(plot, canvasDims);
            }
        }
    }
    drawPlot(plot, canvasDims) {
        ChartComponent.updatePlotRange(plot, this.attachPoint.canvasDims);
        this.renderer.onBeforePlotDraw(plot, canvasDims);
        plot.updateStrategy.update(canvasDims, this.renderer);
        this.renderer.onAfterPlotDraw();
    }
    updateInteractionHandlers(plot) {
        rc_js_util_1._Iterator.consumeAll(this.updateInteractionHandlersIncremental(plot));
    }
    *updateInteractionHandlersIncremental(plot) {
        var _a, _b;
        const startTime = performance.now();
        const canvasDims = this.attachPoint.canvasDims;
        if (plot != null) {
            if (this.plotCollection.has(plot)) {
                const it = ChartComponent.updateInteractionHandler(plot, canvasDims, startTime);
                while (!((_a = it.next().done) !== null && _a !== void 0 ? _a : false)) {
                    yield;
                }
            }
        }
        else {
            const plots = this.plotCollection.getArray();
            for (let i = 0, iEnd = plots.length; i < iEnd; ++i) {
                const plot = plots[i];
                if (this.plotCollection.has(plot)) {
                    const it = ChartComponent.updateInteractionHandler(plot, canvasDims, startTime);
                    while (!((_b = it.next().done) !== null && _b !== void 0 ? _b : false)) {
                        yield;
                    }
                }
            }
        }
    }
    resize() {
        const previousDims = this.attachPoint.canvasDims;
        const canvasDims = this.attachPoint.resizeCanvas();
        if (previousDims == canvasDims) {
            // no resize actually happened, don't emit
            return canvasDims;
        }
        this.contextAdapter.onResize(canvasDims);
        on_canvas_resized_1.OnCanvasResized.emit(this.eventService, canvasDims);
        return canvasDims;
    }
    addPlot(plot) {
        if (this.plotCollection.has(plot)) {
            return plot;
        }
        this.plotCollection.add(plot);
        this.eventService
            .getCategory(on_canvas_resized_1.OnCanvasResized)
            .addListener(plot);
        on_plot_attach_changed_1.OnPlotAttachChanged.emit(plot, this, true);
        return plot;
    }
    removePlot(plot) {
        this.eventService
            .getCategory(on_canvas_resized_1.OnCanvasResized)
            .removeListener(plot);
        plot.clearInteractionHandler();
        this.plotCollection.delete(plot);
        on_plot_attach_changed_1.OnPlotAttachChanged.emit(plot, this, false);
    }
    getPlots() {
        return this.plotCollection.getArray();
    }
    getTransformProvider(transformsToInitialize, missIsDebugError = false) {
        if (this.transformProvider != null) {
            return this.transformProvider;
        }
        return this.transformProvider = new chart_transform_factory_1.ChartTransformFactory(this.renderer, transformsToInitialize, missIsDebugError);
    }
    static updateInteractionHandler(plot, canvasDims, startTime) {
        ChartComponent.updatePlotRange(plot, canvasDims);
        const interactionHandler = plot.getInteractionHandler();
        return interactionHandler == null
            ? rc_js_util_1._Iterator.emptyIterator
            : interactionHandler.update(canvasDims, startTime);
    }
    static updatePlotRange(plot, canvasDims) {
        if (plot.plotRange.isDirty) {
            plot.plotRange.update(canvasDims);
        }
    }
    createDefaultFrameProvider(config) {
        return new frame_provider_1.FrameProvider((plot) => this.updateImmediate(plot), (plot) => this.updateInteractionHandlersIncremental(plot), config.updateOptions.interactionRollupTime);
    }
    registerEventHandlers() {
        on_renderer_context_lost_1.OnRendererContextLost.registerListener(this.eventService, () => {
            this.contextLost = true;
            this.renderer.onContextLost();
            (0, emit_to_plots_1.emitToPlots)(this, on_renderer_context_lost_1.OnRendererContextLost);
        });
        on_renderer_context_restored_1.OnRendererContextRestored.registerListener(this.eventService, () => {
            const context = this.contextAdapter.getContext();
            if (context != null) {
                this.contextLost = false;
                this.renderer.onContextRegained(context);
                (0, emit_to_plots_1.emitToPlots)(this, on_renderer_context_restored_1.OnRendererContextRestored);
            }
        });
        // local listeners
        on_plot_requires_update_1.OnPlotRequiresUpdate.registerListener(this.eventService, (plot, updateFlag) => {
            const updateInteractionHandler = (updateFlag & e_entity_update_flag_1.EEntityUpdateFlag.InteractionHandlerUpdateRequired) !== 0;
            if (updateFlag & e_entity_update_flag_1.EEntityUpdateFlag.DrawRequired) {
                this.frameProvider.updateOnNextFrame(plot, updateInteractionHandler);
            }
            else if (updateInteractionHandler) {
                // FIXME do this with yielding option
                this.updateInteractionHandlers(plot);
            }
        });
        on_entity_added_1.OnEntityAdded.registerListener(this, (entity) => this.attachedEntities.add(entity));
        on_entity_removed_1.OnEntityRemoved.registerListener(this, (entity) => this.attachedEntities.remove(entity));
        // events to proxy to plots
        on_canvas_resized_1.OnCanvasResized.registerListener(this.eventService, (canvasDims) => {
            const plots = this.plotCollection.getArray();
            const emit = on_canvas_resized_1.OnCanvasResized.emit;
            for (let i = 0, iEnd = plots.length; i < iEnd; ++i) {
                emit(plots[i].eventService, canvasDims);
            }
        });
        on_dpr_changed_1.OnDprChanged.registerListener(this.eventService, (dpr) => {
            const plots = this.plotCollection.getArray();
            const emit = on_dpr_changed_1.OnDprChanged.emit;
            for (let i = 0, iEnd = plots.length; i < iEnd; ++i) {
                emit(plots[i].eventService, dpr);
            }
        });
        on_graphics_component_added_1.OnGraphicsComponentAdded.registerListener(this.eventService, (graphicsComponent) => {
            if (this.transformProvider != null) {
                this.transformProvider.initializeTransformComponent(graphicsComponent);
            }
        });
    }
}
exports.ChartComponent = ChartComponent;
//# sourceMappingURL=chart-component.js.map